import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { TranscriptEditorStore } from '../../TranscriptEditorStore';

interface SpeakerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editorStore: TranscriptEditorStore;
  // For editing mode
  speakerToEdit?: {
    speakerLabel: string;
    datoSpeakerId: string;
    name: string;
    title?: string;
    bio?: string;
    voice?: string;
  };
  modalOptions?: {
    addToStory: boolean;
    addToOrganization: boolean;
  };
  skipAssignment?: boolean; // For new speaker creation
}

/**
 * Unified modal for creating new speakers or editing existing ones
 */
const SpeakerFormModal: React.FC<SpeakerFormModalProps> = ({
  isOpen,
  onClose,
  editorStore,
  speakerToEdit,
  skipAssignment = false,
  modalOptions
}) => {
  const isEditMode = !!speakerToEdit;
  
  // Initialize state with either existing speaker data or empty values
  const [name, setName] = useState(speakerToEdit?.name || '');
  const [title, setTitle] = useState(speakerToEdit?.title || '');
  const [bio, setBio] = useState(speakerToEdit?.bio || '');
  const [voice, setVoice] = useState(speakerToEdit?.voice || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add refs for modal container and first input
  const modalRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Handle escape key, auto-focus, and event propagation
  useEffect(() => {
    if (!isOpen) return;

    // Handle escape key to close modal
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Auto-focus the name input when modal opens
    if (nameInputRef.current) {
      // Small delay to ensure the modal is fully rendered
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 50);
    }

    // Prevent selection changes when interacting with the modal
    const handleModalEvents = (e: Event) => {
      e.stopPropagation(); // Prevent event from bubbling up
    };

    // Get the modal and overlay elements
    const modalElement = modalRef.current;
    const overlayElement = modalElement?.parentElement; // The ModalOverlay is parent of ModalContent

    // Add event listeners to the modal content
    if (modalElement) {
      modalElement.addEventListener('mousedown', handleModalEvents, true);
      modalElement.addEventListener('mouseup', handleModalEvents, true);
      modalElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add event listeners to the modal overlay
    if (overlayElement) {
      overlayElement.addEventListener('mousedown', handleModalEvents, true);
      overlayElement.addEventListener('mouseup', handleModalEvents, true);
      overlayElement.addEventListener('mousemove', handleModalEvents, true);
    }

    // Add escape key handler
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      // Clean up all event listeners
      document.removeEventListener('keydown', handleEscapeKey);

      // Clean up modal content listeners
      if (modalElement) {
        modalElement.removeEventListener('mousedown', handleModalEvents, true);
        modalElement.removeEventListener('mouseup', handleModalEvents, true);
        modalElement.removeEventListener('mousemove', handleModalEvents, true);
      }

      // Clean up overlay listeners
      if (overlayElement) {
        overlayElement.removeEventListener('mousedown', handleModalEvents, true);
        overlayElement.removeEventListener('mouseup', handleModalEvents, true);
        overlayElement.removeEventListener('mousemove', handleModalEvents, true);
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Speaker name is required');
      return;
    }

    if (!editorStore) {
      setError('Editor store not available');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const speakerService = editorStore.editor?.getSpeakerService();
      if (!speakerService) {
        throw new Error('Speaker service not available');
      }

      if (isEditMode && speakerToEdit) {
        if (speakerToEdit.datoSpeakerId) {
          // Update existing Dato speaker with all form fields
          const success = await speakerService.updateSpeakerProperties(
            speakerToEdit.datoSpeakerId,
            {
              name,
              title,
              bio,
              voice
            }
          );

          if (!success) {
            throw new Error('Failed to update speaker');
          }
        } else {
          // This is an anonymous speaker (has speakerLabel but no datoSpeakerId)
          // We can use the built-in createAndAssignSpeaker method with the existingSpeakerLabel parameter

          if (speakerToEdit.speakerLabel) {
            // Use the standard method with the existingSpeakerLabel parameter
            // When addToStory is true, we should NOT skip story addition
            const skipStoryAddition = modalOptions?.addToStory === false;

            const success = await editorStore.createAndAssignSpeaker(
              name,
              title,
              bio,
              voice,
              false, // Don't skip assignment
              skipStoryAddition,
              speakerToEdit.speakerLabel // Pass the anonymous speaker label
            );

            if (!success) {
              throw new Error('Failed to create and assign speaker to anonymous speaker');
            }
          } else {
            throw new Error('Missing speaker label for anonymous speaker');
          }
        }
      } else {
        // Create new speaker
        // When addToStory is true, we should NOT skip story addition
        const skipStoryAddition = modalOptions?.addToStory === false;
        console.log('creating new speaker', skipStoryAddition, skipAssignment, modalOptions)

        // Use the standard method if no custom options
        const success = await editorStore.createAndAssignSpeaker(
          name,
          title,
          bio,
          voice,
          skipAssignment,
          skipStoryAddition
        );

        if (!success) {
          throw new Error(skipAssignment ? 'Failed to create speaker' : 'Failed to create and assign speaker');
        }
      }
      

      // Reset form and close modal on success
      setName('');
      setTitle('');
      setBio('');
      setVoice('');
      onClose();
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} speaker:`, err);
      setError(`Failed to ${isEditMode ? 'update' : 'create'} speaker. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay
      onClick={(e) => {
        // Close the modal when clicking the overlay background
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      data-selection-menu="true"
      style={{ userSelect: 'none' }}
      contentEditable={false}
    >
      <ModalContent
        ref={modalRef}
        data-selection-menu="true"
        style={{ userSelect: 'none' }}
        contentEditable={false}
      >
        <ModalHeader>
          <h3>
            {isEditMode
              ? (speakerToEdit?.datoSpeakerId
                  ? 'Edit Speaker'
                  : 'Create & Assign Speaker')
              : 'Create New Speaker'}
          </h3>
          <CloseButton
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            &times;
          </CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <form
            onSubmit={(e) => {
              e.stopPropagation(); // Don't let the submit event propagate
              handleSubmit(e);
            }}
          >
            <FormGroup>
              <Label htmlFor="speakerName">Speaker Name*</Label>
              <Input
                id="speakerName"
                ref={nameInputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter speaker name"
                autoFocus
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="speakerTitle">Title/Role</Label>
              <Input
                id="speakerTitle"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="E.g., CEO, Product Manager, etc."
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="speakerBio">Biography</Label>
              <Textarea
                id="speakerBio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Brief biography of the speaker"
                rows={3}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="speakerVoice">Voice Description</Label>
              <Textarea
                id="speakerVoice"
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                placeholder="How does this speaker talk? Tone, style, etc."
                rows={3}
              />
              <HelpText>Describe the speaker's tone, style, and other voice characteristics. This will help AI generate content in their voice.</HelpText>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <ButtonGroup>
              <CancelButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Cancel
              </CancelButton>
              <SubmitButton
                type="submit"
                disabled={isSubmitting}
                onClick={(e) => {
                  e.stopPropagation();
                  // Don't preventDefault as we want form submission to occur
                }}
              >
                {isSubmitting
                  ? (isEditMode
                      ? (speakerToEdit?.datoSpeakerId ? 'Saving...' : 'Creating & Assigning...')
                      : 'Creating...')
                  : (isEditMode
                      ? (speakerToEdit?.datoSpeakerId ? 'Save Changes' : 'Create & Assign Speaker')
                      : (skipAssignment ? 'Create Speaker' : 'Create & Map Speaker'))}
              </SubmitButton>
            </ButtonGroup>
          </form>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #1a1a2a;
  border-radius: 6px;
  width: 500px;
  max-width: 90vw;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #2d2d42;
  
  h3 {
    color: #f2d093;
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #a9a9a9;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    color: #ffffff;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  box-sizing: border-box;
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #f3e9d7;
  font-size: 14px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid #2d2d42;
  background-color: #12121e;
  color: #f3e9d7;
  font-size: 14px;
  box-sizing: border-box;
  user-select: text;
  cursor: text;

  &:focus {
    outline: none;
    border-color: #f2d093;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid #2d2d42;
  background-color: #12121e;
  color: #f3e9d7;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  user-select: text;
  cursor: text;

  &:focus {
    outline: none;
    border-color: #f2d093;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #a9a9a9;
  margin-top: 4px;
  font-style: italic;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CancelButton = styled(Button)`
  background-color: transparent;
  border: 1px solid #484848;
  color: #f3e9d7;
  
  &:hover {
    background-color: rgba(242, 208, 147, 0.1);
  }
`;

const SubmitButton = styled(Button)`
  background-color: #f2d093;
  border: 1px solid #f2d093;
  color: #030419;
  
  &:hover {
    background-color: #d9b77e;
  }
  
  &:disabled {
    background-color: #6c6c6c;
    border-color: #6c6c6c;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6961;
  font-size: 14px;
  margin-top: 8px;
`;

export default SpeakerFormModal;